import { NotificationEvent } from './../../core/models/notification/notification-event';
import { PushNotificationService } from './../../core/services/api/push-notification.service';
import { MenuService } from './../../core/services/menu.service';
import { Menu } from './../../core/models/menu/menu';
import { StorageService } from './../../core/services/storage.service';
import { AuthService } from './../../core/services/auth/auth.service';
import { Role } from '../../core/enum/role.enum';
import { UserProfile } from './../../core/models/user/user-profile';
import { AccountService } from './../../core/services/api/account.service';
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  DOCUMENT
} from '@angular/core';
import { Router, NavigationEnd, NavigationStart, UrlSerializer } from '@angular/router';
import {
  isPlatformBrowser,
  isPlatformServer,
  Location,
  PopStateEvent
} from '@angular/common';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { BlockUiService } from './../../shared/services/blockUi/block-ui.service';
import { MyApplicationFilterService } from './../../pages/raise/my-application/my-application-filter.service';
import { CampaignSectionFilterService } from './../../pages/landing/campaign-section/campaign-section-filter.service';
import { PopupService } from './../../shared/services/popup/popup.service';
import { NotificationsService } from '../../core/services/api/notifications';
import { finalize } from 'rxjs/operators';
import { ErrorService } from '../../core/services/error.service';
import { IncomingNotification } from '../../core/models/notification/incoming-notification';
import { DefaultProfileImage } from '../../core/services/constant';
import { GtmDataLayerService } from '../../core/services/gtm-data-layer.service';
import { EkycStatus } from '../../core/enum/ekyc-status.enum';
import { serializeRequiredSections } from '../../pages/profile-review-attest/required-section';
import { MenuItem } from '../../core/models/menu/menu-item';
import { TabMenuItem } from '../../core/models/menu/tab-menu-item';
import { environment } from '../../../environments/environment';
import moment from 'moment';
import { WalletService } from '../../core/services/api/mysec/wallet.service';
import { Wallet } from '../../core/models/mysec/wallet';
import { PstxAccountService } from '../../core/services/pstx-account.service';
import { CokeepsService } from '../../core/services/cokeeps.service';
import { FeatureFlagService } from '../../core/services/feature-flag.service';
import { SameUrlTraversalTracker } from '../../shared/utils/same-url-traversal';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    standalone: false
})
export class NavbarComponent implements OnInit {
  @ViewChild('profileDropdown', { static: false, read: NgbDropdown }) profileDropdown: NgbDropdown;
  @ViewChild('notificationDropdown', { static: false, read: NgbDropdown })
  notificationDropdown: NgbDropdown;
  @ViewChild('managementDropdown', { static: false, read: NgbDropdown })
  managementDropdown: NgbDropdown;
  @ViewChild('dropdownRef', { static: false, read: NgbDropdown }) navDropdown: NgbDropdown;
  public isCollapsed = true;
  private lastPoppedUrl: string;
  private yScrollStack: number[] = [];
  private notificationSubscription: Subscription;

  profile: UserProfile;
  menu: Menu;
  // #80: the Learn dropdown is reshaped by the Knowledge Centre flag (Learning Portal + FAQ merged
  // into one "Knowledge Centre" item) without mutating the shared menu.mainMenu array. Built once in
  // ngOnInit from the settled flag; OFF keeps today's legacy three-item dropdown unchanged.
  mainMenu: MenuItem[];
  // FRC-858: Dashboard sidebar pages hosted outside /dashboard (e.g. /pstx/account); see
  // collectDashboardOwnedRoutes. Built once in ngOnInit from the shared menu.
  private dashboardOwnedRoutes: string[] = [];
  role: Role;
  incomingNotification: boolean = false;
  notifications: IncomingNotification[];
  notificationLoadMore = true;
  params = {
    skip: 0,
    take: 10,
  };
  isLoading = false;
  registerInProgress = false;
  defaultProfileImage = DefaultProfileImage;
  signUpLink: string;

  Role: typeof Role = Role;
  EkycStatus: typeof EkycStatus = EkycStatus;
  showNotificationBar: boolean = false;

  isDevelopmentMode: boolean = environment.environment !== 'PRODUCTION';
  environment = environment.environment;

  wallet: Wallet;
  isWalletFound: boolean = true;
  showTopUp: boolean = true;

  isCokeepsEnabled: boolean = environment.flagToUseCokeeps ?? false;

  // #77 / #80 Knowledge Centre flag. When ON, the Learn dropdown merges the legacy "Learning Portal"
  // + "FAQ" items into one internal "Knowledge Centre" link at /learn (the canMatch guard swaps the
  // page); Academy + About pitchIN stay. When OFF (DEFAULT) the legacy dropdown is unchanged. Drives
  // MenuService.getMainMenu below. Loaded at bootstrap (APP_INITIALIZER); defaults false.
  isKnowledgeCentreEnabled: boolean = false;

  constructor(
    public location: Location,
    public storageService: StorageService,
    private readonly router: Router,
    private readonly accountService: AccountService,
    private readonly authService: AuthService,
    private readonly menuService: MenuService,
    private readonly pushNotificationService: PushNotificationService,
    @Inject(PLATFORM_ID) private readonly platform: Object,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly blockUiService: BlockUiService,
    private readonly myApplicationFilterService: MyApplicationFilterService,
    private readonly campaignSectionFilterService: CampaignSectionFilterService,
    private readonly popupService: PopupService,
    private readonly notificationsService: NotificationsService,
    private readonly errorService: ErrorService,
    private readonly _gtmDataLayerService: GtmDataLayerService,
    private readonly renderer: Renderer2,
    private readonly walletService: WalletService,
    private readonly pstxAccountService: PstxAccountService,
    private readonly cokeepsService: CokeepsService,
    private readonly featureFlagService: FeatureFlagService,
  ) {}

  async ngOnInit() {
    this.menu = this.menuService.menu;
    this.dashboardOwnedRoutes = this.collectDashboardOwnedRoutes(this.menu.investorDashboardMenu);
    // #77 / #80: the flag is loaded at bootstrap (APP_INITIALIZER), so this read is settled here.
    this.isKnowledgeCentreEnabled = this.featureFlagService.knowledgeCentreEnabled;
    // #80: flag-aware Learn dropdown. ON merges Learning Portal + FAQ into one Knowledge Centre
    // item (Academy + About pitchIN kept); OFF returns the shared mainMenu untouched.
    this.mainMenu = this.menuService.getMainMenu(this.isKnowledgeCentreEnabled);

    if (isPlatformServer(this.platform)) return;

    if (location.pathname == '/redirect-login') return;

    this.accountService.currentUser.subscribe((profile) => {
      this.profile = profile;
      this.showTopUp = this.pstxAccountService.canShowTopUp(this.role, this.profile);

      if (this.authService.isAuthenticated()) {
        if (!this.profile) {
          this.accountService.profile(true).subscribe({
            error: () => this.checkFlow(),
          });
        } else {
          this.checkFlow();
        }
      }
    });

    // FRC-749: a BACK/FORWARD onto the URL already showing (the investor bottom sheet's own history
    // entry) is not a page change — leave the scroll position and the scroll stack alone.
    const sameUrlTraversals = new SameUrlTraversalTracker();

    this.router.events.subscribe(async (event) => {
      if (sameUrlTraversals.check(event)) {
        if (event instanceof NavigationEnd) this.lastPoppedUrl = undefined;
        return;
      }

      if (event instanceof NavigationStart) {
        if (event.url != this.lastPoppedUrl) this.yScrollStack.push(window.scrollY);
      } else if (event instanceof NavigationEnd) {
        if (event.url == this.lastPoppedUrl) {
          this.lastPoppedUrl = undefined;

          if (isPlatformBrowser(this.platform)) window.scrollTo(0, this.yScrollStack.pop());
        } else if (isPlatformBrowser(this.platform)) {
          window.scrollTo(0, 0);

          var navbar = document.getElementsByTagName('nav')[0];

          if (navbar?.classList.contains('headroom--unpinned')) {
            navbar.classList.remove('headroom--unpinned');
            navbar.classList.add('headroom--pinned');
          }
        }

        if (this.profile) {
          // Push the user_id variable to gtm only when profile exists / (is authenticated)
          this._gtmDataLayerService.logUser(
            this.profile.id,
            this.role,
            this.profile.ekycStatus == EkycStatus.Verified,
            this.profile.hasTransaction,
            this.profile.email,
          );
        }
      }
    });

    this.location.subscribe((ev: PopStateEvent) => {
      this.lastPoppedUrl = ev.url;
    });

    this.accountService.notifications.subscribe((notifications) => {
      if (!notifications) {
        this.notifications = [];
        return;
      }

      if (notifications.length == 0) {
        this.notificationLoadMore = false;
        return;
      }

      this.notifications = this.notifications.concat(notifications);
      this.incomingNotification = !!this.notifications.find(
        (notificatiion) => !notificatiion.isRead,
      );
    });

    this.notificationSubscription = this.pushNotificationService.event.subscribe(
      (event: NotificationEvent) => {
        this.incomingNotification = true;
        this.getNotifications(true);
      },
    );

    this.pstxAccountService.getWallet().subscribe((wallet: Wallet) => {
      this.wallet = wallet;
    });

    this.pstxAccountService.getIsWalletFound().subscribe((isFound: boolean) => {
      this.isWalletFound = isFound;
    });

    this.initNotificationBar();

    // Avoid to restore the session before the setting the secret properly
    // Also, DO NOT add this code into accountService, or router .subscribe() as the code will be initiate whenever the new event emits
    if (this.authService.isAuthenticated() && this.isCokeepsEnabled) {
      await this.cokeepsService.restoreSession();
    }
  }

  initNotificationBar() {
    // Will check the device date time if is > Malaysia Time 1am will be hidden;
    const dateTimeToRemove = new Date('2023-06-01T01:00:00.000+08:00');
    const isShow = moment().isBefore(dateTimeToRemove);

    this.showNotificationBar = isShow;

    if (isShow) {
      this.renderer.addClass(this.document.body, 'open-notification-bar');
    }
  }

  checkFlow() {
    this.accountService.getSignUpFlowStatus().subscribe({
      next: (flowData) => {
        this.registerInProgress = flowData && !flowData.isCompleted;
        this.role = this.storageService.role;

        if (this.profile && !this.registerInProgress) {
          if (this.profile.completeEkyc) {
            // INC-284: the investing/eKYC gate re-points to the review-and-attest screen,
            // requiring the identity + contact sections before the investor can proceed.
            this.router.navigate(['/profile/review-attest'], {
              queryParams: {
                required: serializeRequiredSections(['identity', 'contact']),
                returnUrl: this.router.url,
              },
            });
          }

          this.getNotifications(true);

          this.getWalletBalance();
        } else {
          let role =
            this.role == Role.CompanyRep
              ? 'company-rep'
              : this.role == Role.IndividualInvestor
                ? 'individual-investor'
                : 'issuer-rep';

          this.signUpLink = '/sign-up/' + role;
          if (this.router.url !== this.signUpLink) this.router.navigate([this.signUpLink]);
        }
      },
      error: (err) => {
        this.errorService.showError(err);
      },
    });
  }

  getNotifications(reload?: boolean) {
    if (reload) {
      this.params.skip = 0;
      this.notifications = [];
    }

    this.isLoading = true;

    this.notificationsService
      .getNotifications(this.params.take, this.params.skip)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (data) => {},
        (err) => {
          this.errorService.showError(err);
        },
      );
  }

  getWalletBalance(forceRefresh: boolean = false) {
    if (this.role === Role.IssuerRep) return;

    if (!forceRefresh && (this.wallet || !this.isWalletFound)) return;

    this.pstxAccountService.getWallet();
  }

  resetWallet() {
    this.pstxAccountService.resetState();
  }

  isActiveMenu(url: string, isExactMatch: boolean = false) {
    if (!isPlatformBrowser(this.platform)) {
      return false;
    }

    const path = window?.location?.pathname;

    if (!path) {
      return false;
    }

    if (isExactMatch) {
      return path === url;
    }

    return path.startsWith(url);
  }

  isActiveSubmenu(menu: MenuItem[]) {
    if (!menu) return false;

    const isActive = menu.some((m) => {
      return this.isActiveMenu(m.url, true);
    });

    return isActive;
  }

  // FRC-858: a main-menu tab is underlined by prefix match, so /pstx/account (a Dashboard sidebar
  // page) used to light up Trade (/pstx). Dashboard-owned pages belong to the Dashboard tab, so no
  // main-menu tab claims them.
  isActiveMainMenu(menu: MenuItem) {
    if (this.isDashboardOwnedRoute()) return false;

    return this.isActiveMenu(menu.url) || this.isActiveSubmenu(menu.submenus);
  }

  // FRC-858: the Dashboard tab also owns its sidebar pages that live outside /dashboard.
  isActiveDashboardMenu() {
    return this.isActiveMenu('/dashboard') || this.isDashboardOwnedRoute();
  }

  private isDashboardOwnedRoute() {
    return this.dashboardOwnedRoutes.some((url) => this.isActiveMenu(url, true));
  }

  // Internal routes reachable from the investor Dashboard sidebar but hosted outside /dashboard
  // (today just /pstx/account). Derived from the sidebar menu so future entries are covered too.
  // `submenus` is optional, so the recursive call genuinely passes undefined on leaf items.
  private collectDashboardOwnedRoutes(menus?: TabMenuItem[]): string[] {
    if (!menus?.length) return [];

    return menus.flatMap((menu) => [
      ...(menu.url?.startsWith('/') && !menu.url.startsWith('/dashboard') && !menu.isExternal
        ? [menu.url]
        : []),
      ...this.collectDashboardOwnedRoutes(menu.submenus),
    ]);
  }

  handleLoginRedirection(event: MouseEvent) {
    const currentUrl = this.router.url;

    // Retain the `href` from <a> for SEO purpose, but avoid it navigate itself
    event.preventDefault();

    // Avoid doing any redirection when in /login
    if (currentUrl.includes('/login')) return;

    const excludedUrls = ['/sign-up'];
    const returnUrl = excludedUrls.some((url) => currentUrl.includes(url)) ? null : currentUrl;
    const queryParams = returnUrl ? { returnUrl } : null;

    this.router.navigate(['/login'], { queryParams }).catch((err) => {
      console.error('fail to navigate to login: ', err);
    });
  }

  logout() {
    this.isCollapsed = true;

    this.popupService.confirm('common.confirmLogout').then(
      () => {
        this.blockUiService.open();

        this.accountService
          .logout()
          .subscribe(() => {
            this.router.navigate(['/']);
            this.registerInProgress = false;
            this._gtmDataLayerService.logUser();
            this.resetWallet();
          })
          .add(() => {
            this.blockUiService.close();
          });
      },
      () => {},
    );
  }

  // ENB-70: the copy editor's logout is an <a> with no href, which browsers keep out of the tab
  // order and do not activate on Enter/Space. It is that session's ONLY way out, so it is bound to
  // click and to both keys through here rather than repeating the pair of calls in the template.
  logoutTranslationEditor(event?: Event) {
    event?.preventDefault();
    this.logout();
    this.clearServiceFilter(false);
  }

  clearServiceFilter(isParent: boolean) {
    if (!isParent) {
      this.isCollapsed = true;
    }

    this.myApplicationFilterService.clear();
    this.campaignSectionFilterService.clear();
  }

  viewDocument(url: string) {
    if (typeof window !== 'undefined') {
      window.open(url);
    }
  }

  deleteNotification(id: number) {
    this.popupService.confirm('common.confirmDeleteNotification').then(
      () => {
        this.blockUiService.open();

        this.notificationsService
          .deleteNotification(id)
          .pipe(finalize(() => this.blockUiService.close()))
          .subscribe({
            next: () => {
              this.getNotifications(true);
            },
            error: (err) => {
              this.errorService.showError(err);
            },
          });
      },
      () => {},
    );
  }

  markAllIsRead() {
    this.blockUiService.open();

    this.notificationsService
      .markAllNotificationIsRead()
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe({
        next: () => {
          this.getNotifications(true);
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  markIsRead(notification: IncomingNotification) {
    this.blockUiService.open();

    this.notificationsService
      .markNotificationIsRead(notification.id)
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe({
        next: () => {
          this.getNotifications(true);

          if (notification.notification.deepLink)
            window.open(notification.notification.deepLink, '_self');
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  onNotificationScroll() {
    if (this.notificationLoadMore == false) return;

    this.params.skip += this.params.take;
    this.getNotifications();
  }

  closeDropdown() {
    if (isPlatformBrowser(this.platform)) {
      if (this.navDropdown) this.navDropdown.close();

      if (this.profileDropdown) this.profileDropdown.close();

      if (this.notificationDropdown) this.notificationDropdown.close();

      if (this.managementDropdown) this.managementDropdown.close();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    this.closeDropdown();
  }

  disableSlide(event: TouchEvent): void {
    event.preventDefault();
  }
}
